import { acceptHMRUpdate, defineStore } from 'pinia';
import { callFunc } from '@/utils/net';
import { useTRPCAsync } from '../composable/trpc';
import type { ProductVariant } from '@prisma/client';
import type { CartType } from '@shared/models/cart';

function clearAllCookies() {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=; Max-Age=-99999999; path=/`;
  }
}

export interface UserType {
  firstName: string;
  lastName: string;
  email: string;
  status: number;
  dob?: Date;

  outletId?: string;

  roles: string[];
}

export interface UserSession {
  id: string;
  username?: string;
  accessToken: string | null;
  refreshToken: string | null;
  avatar?: string;
  teams: string[];
  profile: UserType;
  outletId?: string | null;
  providerType: string;
  cartSessionToken?: string | null;
  jwt?: string | null;
  siteId?: string | null;

  country?: string;
}

const defaultCart = (): CartType => {
  return {
    status: null,
    id: '',
    ownerUserId: null,
    teams: [],
    amount: 0,
    carrierId: null,
    orderTotal: 0,
    outletId: null,
    paymentMethod: null,
    items: [],
    payments: [],
    voucherAmount: 0,
    shippingVoucherAmount: 0,
    shippingAmount: 0,
    taxAmount: 0,
    vouchers: []
  };
};

const guestUser = () => {
  return {
    id: '',
    accessToken: null,
    refreshToken: null,
    avatar: undefined,
    teams: [],
    profile: {
      firstName: '',
      lastName: '',
      email: '',
      status: 0,
      outletId: '',
    },
    siteId: null,
    providerType: '',
    cartSessionToken: '',
  };
};

export const useAclStore = defineStore('acl', {
  state: (): {
    loading: boolean;
    currentUser: UserSession;
    loginError: string;
    cartSessionToken: string;
    cart: CartType;
    cartVariants: ProductVariant[];
    jwt: string;
  } => {
    return {
      loading: false,
      currentUser: {} as UserSession,
      loginError: '' as string,
      cartSessionToken: '' as string,
      cart: defaultCart(),
      cartVariants: [] as ProductVariant[],
      jwt: '',
    };
  },

  getters: {
    currentUserName: (state) => {
      const data = state.currentUser?.profile;
      console.log('currentUserName', state.currentUser, data);
      if (state.currentUser && data) {
        if (data.firstName && data.lastName) {
          return `${data.firstName} ${data.lastName}`;
        }
        return data.firstName;
      }
      if (data?.email) {
        return data.email;
      }
      return 'Guest';
    },
    siteCurrencyLabel: () => {
      // TODO
      return 'RM';
      // if (state.currentUser && state.currentUser.profile) {
      //   return state.currentUser.profile?.currency
      // }
      // return 'USD'
    },
  },
  actions: {
    async logout() {
      try {
        const token = localStorage.getItem('jwt_token');
        if (token) {
          await fetch(`${import.meta.env.NUXT_PUBLIC_V2_API_BASE}/auth/jwt/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
        }
      } catch (err) {
        console.error('Logout error:', err);
      } finally {
        localStorage.removeItem('jwt_token');
        this.jwt = '';
        this.currentUser = guestUser();
        this.clearCart();
        clearAllCookies();
      }
    },

    async checkSession() {
      const token = localStorage.getItem('jwt_token');
      if (!token) {
        return null;
      }

      try {
        const response = await fetch(`${import.meta.env.NUXT_PUBLIC_V2_API_BASE}/auth/jwt/verify`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          localStorage.removeItem('jwt_token');
          return null;
        }

        const { user } = await response.json();
        this.jwt = token;
        this.currentUser = {
          id: user.id,
          username: user.name,
          avatar: user.pictureUrl,
          accessToken: token,
          refreshToken: null,
          teams: user.teams,
          profile: user,
          providerType: 'jwt',
          jwt: token,
        };

        return this.currentUser;
      } catch (err) {
        console.error('Session check failed:', err);
        localStorage.removeItem('jwt_token');
        return null;
      }
    },
    clearCart() {
      this.cart = defaultCart();
      return callFunc('clearCart', { sessionToken: this.cartSessionToken });
    },
    async setCartSession(token: string, cart?: CartType | null, cartVariants?: ProductVariant[]) {
      this.cartSessionToken = token;
      this.currentUser.cartSessionToken = token;
      if (cart) {
        this.cart = cart;
      }
      if (cartVariants) {
        this.cartVariants = cartVariants;
      }
    },

    async refetchCurrentUser() {
      if (!this.jwt) {
        return
      }
      
      const res = await useEden((client) => {
        return client.api.v2.my.profile.get()
      })
      const profile = res?.data;
      console.log('refetchCurrentUser', profile)
      const user = profile.user;
      
      this.currentUser.id = user.id;
      this.currentUser.profile = user;
      this.currentUser.teams = user.teams;
      this.currentUser.providerType = 'local';
      this.cartSessionToken = user.cartSessionToken;
    },
    async setTeams(teams: string[]) {
      this.currentUser.teams = teams;
    },
    async loadCart(sessionToken?: string): Promise<{ cart: CartType; cartVariants: ProductVariant[] } | null> {
      // if sessionToken === undefined means we are loading the cart from the current user
      console.log('loadcart', sessionToken ? this.cartSessionToken : sessionToken);
      const res = await fetchData(`/api/v2/cart/get/${this.cartSessionToken ?? sessionToken}`)
      console.log('loadCart ok', res)
      if (!res) {
        return null;
      }

      this.cart = res.cart;
      this.cartVariants = res.variants || [];

      // console.log('acl.loadCart', { cart: res.cart })

      if (this.cartSessionToken !== res.cart.sessionToken) {
        await this.setCartSession(res.cart.sessionToken ?? '');
      }

      return { cart: this.cart, cartVariants: this.cartVariants };
    },
    async loginEmail(email: string, pass: string) {
      this.loginError = '';
      this.loading = true;
      const { execute: checkAuth } = useTRPCAsync((client) => {
        return client.me.checkAuth.mutate
      });
      
      try {
        if (!email || !pass) {
          console.log('login?', { email, pass })
          alert('please login with email and password')
          return false
        }
        const { v2ApiBase } = useApiBase()
        // console.log('v2apiBase', v2ApiBase)
        const response = await fetch(`${v2ApiBase}/api/v1/auth/signin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ loginId: email, password: pass }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Login failed');
        }
        const jsonData = await response.json();
        console.log('response', jsonData);

        const { token, user } = jsonData;
        
        this.jwt = token;
        this.currentUser = {
          id: user.id,
          username: user.name,
          avatar: user.pictureUrl,
          accessToken: token,
          refreshToken: null,
          teams: user.teams,
          profile: user,
          providerType: 'jwt',
          jwt: token,
        };

        if (this.currentUser.profile?.dob) {
          this.currentUser.profile.dob = new Date(this.currentUser.profile.dob);
        }

        // Store JWT token in localStorage for persistence
        localStorage.setItem('jwt_token', token);
        
        setTimeout(() => {
          this.refetchCurrentUser();
        }, 1000);
        
        return this.currentUser;
      } catch (err: any) {
        console.error('Login failed:', err);
        this.loginError = err.message || 'Login failed';
        throw err;
      } finally {
        this.loading = false;
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async sendVerifyEmail(_email: string) {
      // this.loading = true
      // try {
      // }
      // finally {
      //   this.loading = false
      // }
    },
  },
  persist: true,
});
